import { cloneDeep } from 'lodash';
import { ErrorReturn } from '../utils/ErrorReturn';
import { init_Mask, preconditionTypeToString } from './initInterfaces';
import {
  PolicyComponents,
  Claim,
  SelfAssertedProfile,
  NonInteractiveTechnicalProfile,
  InputClaim,
  PersistedClaim,
  OutputClaim,
  DisplayClaim,
  ValidationTP,
  CommonTechnicalProfile,
  PreconditionAction,
  ClaimsTransformation,
  Transformation,
  UserJourney,
  SubJourney,
  ProviderSelect,
  ClaimsExchange,
  Precondition,
  OrchestrationStep,
  Enumeration,
  Pattern,
  Mask,
} from './interfaces';

/**
 *
 * @param {string} xmlString - a string of XML
 * @param {PolicyComponents}policy - the XML deconstructed into usable building blocks
 */
const ns = 'http://schemas.microsoft.com/online/cpim/schemas/2013/06';
export const generateXML = (xmlString: string, policy: PolicyComponents, policyName: string): ErrorReturn | string => {
  // DOMParser to go over XML policy
  const parser = new DOMParser();

  const xml = parser.parseFromString(xmlString, 'text/xml');

  try {
    Object.keys(policy.claims).length > 0 && createXMLClaims(xml, policy.claims, parser);
    Object.keys(policy.claimTransformations).length > 0 &&
      createXMLClaimsTransformations(xml, policy.claimTransformations, parser);
    createXMLTechnicalProfiles(xml, policy.selfAssertedProfiles, policy.otherProfiles, parser);
    createXMLJourneys(xml, policy.userJourneys, parser, 'UserJourney', policyName);
    createXMLJourneys(xml, policy.subJourneys, parser, 'SubJourney', policyName);

    console.log('FINAL XML :', xml);
    return new XMLSerializer().serializeToString(xml);
  } catch (e: any) {
    return e;
  }
};

const addNewClaimsProvider = (xml: Document, claimsProviderName: string): void => {
  let claimsProviders = xml.getElementsByTagName('ClaimsProviders')[0];
  let newParentClaimsProvider = xml.createElementNS(ns, 'ClaimsProvider');

  let displayName = xml.createElementNS(ns, 'DisplayName');
  displayName.innerHTML = claimsProviderName;
  newParentClaimsProvider.appendChild(displayName);

  if (newParentClaimsProvider !== null) {
    claimsProviders.appendChild(newParentClaimsProvider);
  }
};

let getParentClaimsProvider = (xml: Document, claimsProviderName: string): Element | undefined => {
  return Array.from(xml.getElementsByTagName('ClaimsProvider')).find(
    (cp) => cp.getElementsByTagName('DisplayName')[0]?.innerHTML === claimsProviderName,
  );
};

// protocolElem.setAttribute('Name', 'Proprietary');
// protocolElem.setAttribute('Handler', 'Web.TPEngine.Providers.SelfAssertedAttributeProvider, Web.TPEngine, Version=1.0.0.0, Culture=neutral, PublicKeyToken=null');
const generateProtocol = (xml: Document, protocolValues?: [string, string]): Element => {
  if (!protocolValues) {
    protocolValues = [
      'Proprietary',
      'Web.TPEngine.Providers.SelfAssertedAttributeProvider, Web.TPEngine, Version=1.0.0.0, Culture=neutral, PublicKeyToken=null',
    ];
  }

  let protocolElem = xml.createElementNS(ns, 'Protocol');
  protocolElem.setAttribute('Name', protocolValues[0]);
  protocolElem.setAttribute('Handler', protocolValues[1]);
  return protocolElem;
};

const generateMetadata = (xml: Document, newMetadata: Array<[string, string]>): Element => {
  let metadataElem = xml.createElementNS(ns, 'Metadata');

  for (let metadataItem of newMetadata) {
    let itemElem = xml.createElementNS(ns, 'Item');
    itemElem.setAttribute('Key', metadataItem[0]);
    itemElem.innerHTML = metadataItem[1];
    metadataElem.appendChild(itemElem);
  }

  return metadataElem;
};

const generateInputClaims = (xml: Document, newInputClaims: Array<InputClaim>): Element => {
  let inputClaimsElem = xml.createElementNS(ns, 'InputClaims');

  for (let inputClaim of newInputClaims) {
    let inputClaimElem = xml.createElementNS(ns, 'InputClaim');
    inputClaimElem.setAttribute('ClaimTypeReferenceId', inputClaim.claimTypeReferenceId);

    if (inputClaim.partnerClaimType !== '') {
      inputClaimElem.setAttribute('PartnerClaimType', inputClaim.partnerClaimType);
    }

    if (inputClaim.defaultValue !== '') {
      inputClaimElem.setAttribute('DefaultValue', inputClaim.defaultValue);
    }

    if (inputClaim.required === true) {
      inputClaimElem.setAttribute('Required', 'true');
    }

    if (inputClaim.alwaysUseDefaultValue === true) {
      inputClaimElem.setAttribute('AlwaysUseDefaultValue', 'true');
    }

    inputClaimsElem.appendChild(inputClaimElem);
  }

  return inputClaimsElem;
};

const generateDisplayClaims = (xml: Document, newDisplayClaims: Array<DisplayClaim>): Element => {
  let displayClaimsElem = xml.createElementNS(ns, 'DisplayClaims');

  for (let displayClaim of newDisplayClaims) {
    let displayClaimElem = xml.createElementNS(ns, 'DisplayClaim');
    displayClaimElem.setAttribute('ClaimTypeReferenceId', displayClaim.claimTypeReferenceId);

    if (displayClaim.displayControlReferenceId) {
      displayClaimElem.setAttribute('DisplayControlReferenceId', displayClaim.displayControlReferenceId);
    }

    if (displayClaim.required === true) {
      displayClaimElem.setAttribute('Required', 'true');
    }

    displayClaimsElem.appendChild(displayClaimElem);
  }

  return displayClaimsElem;
};

const generatePersistedClaims = (xml: Document, newPersistedClaims: Array<PersistedClaim>): Element => {
  let persistedClaimsElem = xml.createElementNS(ns, 'PersistedClaims');

  for (let persistedClaim of newPersistedClaims) {
    let persistedClaimElem = xml.createElementNS(ns, 'PersistedClaim');
    persistedClaimElem.setAttribute('ClaimTypeReferenceId', persistedClaim.claimTypeReferenceId);

    if (persistedClaim.partnerClaimType !== '') {
      persistedClaimElem.setAttribute('PartnerClaimType', persistedClaim.partnerClaimType);
    }

    if (persistedClaim.defaultValue !== '') {
      persistedClaimElem.setAttribute('DefaultValue', persistedClaim.defaultValue);
    }

    persistedClaimsElem.appendChild(persistedClaimElem);
  }

  return persistedClaimsElem;
};

const generateOutputClaims = (xml: Document, newOutputClaims: Array<OutputClaim>): Element => {
  let outputClaimsElem = xml.createElementNS(ns, 'OutputClaims');

  for (let outputClaim of newOutputClaims) {
    let outputClaimElem = xml.createElementNS(ns, 'OutputClaim');
    outputClaimElem.setAttribute('ClaimTypeReferenceId', outputClaim.claimTypeReferenceId);

    if (outputClaim.partnerClaimType !== '') {
      outputClaimElem.setAttribute('PartnerClaimType', outputClaim.partnerClaimType);
    }

    if (outputClaim.defaultValue !== '') {
      outputClaimElem.setAttribute('DefaultValue', outputClaim.defaultValue);
    }

    if (outputClaim.required === true) {
      outputClaimElem.setAttribute('Required', 'true');
    }

    if (outputClaim.alwaysUseDefaultValue === true) {
      outputClaimElem.setAttribute('AlwaysUseDefaultValue', 'true');
    }

    outputClaimsElem.appendChild(outputClaimElem);
  }

  return outputClaimsElem;
};

const generateClaimsTransformations = (
  xml: Document,
  type: 'InputClaimsTransformation' | 'OutputClaimsTransformation',
  newClaimsTransformation: Array<string>,
): Element => {
  let claimsTransformationXML = xml.createElementNS(ns, 'ClaimTransformations');
  for (let claimTrans of newClaimsTransformation) {
    let claimTransElem = xml.createElementNS(ns, type);
    claimTransElem.setAttribute('ReferenceId', claimTrans);
    claimsTransformationXML.appendChild(claimTransElem);
  }
  return claimsTransformationXML;
};

const generateAllValidationTPsForSelfAsserted = (xml: Document, newValidationTPs: Array<ValidationTP>): Element => {
  let outputClaimsElem = xml.createElementNS(ns, 'OutputClaims');
  let validationTPsElem = xml.createElementNS(ns, 'ValidationTechnicalProfiles');

  for (let validationTP of newValidationTPs) {
    let validationTPelem = xml.createElementNS(ns, 'ValidationTechnicalProfile');
    validationTPelem.setAttribute('ReferenceId', validationTP.id);

    if (validationTP.continueOnError) {
      validationTPelem.setAttribute('ContinueOnError', 'true');
    }

    if (validationTP.continueOnSuccess === false) {
      validationTPelem.setAttribute('ContinueOnSuccess', 'true');
    }

    if (validationTP.preconditions.length > 0) {
      let preconditionsElem = xml.createElementNS(ns, 'Preconditions');

      for (let precon of validationTP.preconditions) {
        let preconditionElem = xml.createElementNS(ns, 'Precondition');

        let preconType = preconditionTypeToString(precon.type);
        preconditionElem.setAttribute('Type', preconType);

        let executeActionsIfValue = precon.executeActionsIf ? 'True' : 'False';
        preconditionElem.setAttribute('ExecuteActionsIf', executeActionsIfValue);

        for (let val of precon.values) {
          let valueElem = xml.createElementNS(ns, 'Value');
          valueElem.innerHTML = val;
          preconditionElem.appendChild(valueElem);
        }

        let actionElem = xml.createElementNS(ns, 'Action');
        actionElem.innerHTML = 'SkipThisValidationTechnicalProfile';
        preconditionElem.appendChild(actionElem);

        preconditionsElem.appendChild(preconditionElem);
      }

      validationTPelem.appendChild(preconditionsElem);
    }

    validationTPsElem.appendChild(validationTPelem);
  }

  return validationTPsElem;
};

const generateSingleValidationTP = (xml: Document, validationTP: ValidationTP): Element => {
  let validationTPelem = xml.createElementNS(ns, 'ValidationTechnicalProfile');
  validationTPelem.setAttribute('ReferenceId', validationTP.id);

  if (validationTP.continueOnError) {
    validationTPelem.setAttribute('ContinueOnError', 'true');
  }

  if (validationTP.continueOnSuccess === false) {
    validationTPelem.setAttribute('ContinueOnSuccess', 'true');
  }

  if (validationTP.preconditions.length > 0) {
    let preconditionsElem = xml.createElementNS(ns, 'Preconditions');

    for (let precon of validationTP.preconditions) {
      let preconditionElem = xml.createElementNS(ns, 'Precondition');

      let preconType = preconditionTypeToString(precon.type);
      preconditionElem.setAttribute('Type', preconType);

      let executeActionsIfValue = precon.executeActionsIf ? 'True' : 'False';
      preconditionElem.setAttribute('ExecuteActionsIf', executeActionsIfValue);

      for (let val of precon.values) {
        let valueElem = xml.createElementNS(ns, 'Value');
        valueElem.innerHTML = val;
        preconditionElem.appendChild(valueElem);
      }

      let actionElem = xml.createElementNS(ns, 'Action');
      actionElem.innerHTML = 'SkipThisValidationTechnicalProfile';
      preconditionElem.appendChild(actionElem);

      preconditionsElem.appendChild(preconditionElem);
    }

    validationTPelem.appendChild(preconditionsElem);
  }

  return validationTPelem;
};

const insertRequiredClaimTextContent = (tagName: string, textContent: string, currentClaim: Element) => {
  let claimToModify = currentClaim.querySelector(tagName);
  if (claimToModify === null) {
    throw new ErrorReturn(
      `There is no element ${tagName} to place ${textContent} in the following element: ${new XMLSerializer().serializeToString(
        currentClaim,
      )}`,
    );
  }
  claimToModify.innerHTML = textContent; //ideal to only set property if it's different
};

const createXMLClaims = (xml: Document, newClaims: Record<string, Claim>, parser: DOMParser) => {
  const currentXMLClaims = Array.from(xml.querySelectorAll('ClaimsSchema>ClaimType'));

  // This was is more inefficient but it preserves any comments
  for (let claim of Object.keys(newClaims)) {
    let filteredClaims = currentXMLClaims.filter((elem) => elem.getAttribute('Id') === newClaims[claim].id);

    // throw error if >1
    if (filteredClaims.length > 0) {
      let claimToModify = filteredClaims[0];
      claimToModify.setAttribute('Id', newClaims[claim].id); // get rid of this?
      insertRequiredClaimTextContent('DisplayName', newClaims[claim].displayName, claimToModify);
      insertRequiredClaimTextContent('DataType', newClaims[claim].dataType, claimToModify);
      if (newClaims[claim].hasOwnProperty('mask')) {
        // update maskXML data
        let currentMaskXML = Array.from(claimToModify.getElementsByTagName('Mask'))[0];
        let newMaskJSON: Mask = newClaims[claim].mask || init_Mask();
        if (newMaskJSON['regex'] != '' && currentMaskXML.getAttribute('regex') !== newMaskJSON['regex']) {
          currentMaskXML.setAttribute('Regex', newMaskJSON['regex']);
        }
        if (newMaskJSON['type'] != '' && currentMaskXML.getAttribute('Type') !== newMaskJSON['type']) {
          currentMaskXML.setAttribute('Type', newMaskJSON['type']);
        }
        if (newMaskJSON['value'] != '' && currentMaskXML.innerHTML !== newMaskJSON['value']) {
          currentMaskXML.innerHTML = newMaskJSON['value'];
        }
      }
      if (['Enumeration', 'Pattern'].includes(newClaims[claim].restriction || '')) {
        if (claimToModify.getElementsByTagName('Restriction').length === 0) {
          let restriction = xml.createElementNS(ns, 'Restriction');
          claimToModify.appendChild(restriction);
        }
        let restriction = claimToModify.getElementsByTagName('Restriction')[0];
        if (newClaims[claim].restriction === 'Enumeration') {
          let currentEnumerationXML = Array.from(restriction.getElementsByTagName('Enumeration'));
          let newEnumerationsJSON = cloneDeep(newClaims[claim].enumeration || []);
          currentEnumerationXML.forEach((enumerationXML) => {
            if (newEnumerationsJSON.length > 0) {
              enumerationXML.setAttribute('Value', newEnumerationsJSON[0]['value']);
              enumerationXML.setAttribute('Text', newEnumerationsJSON[0]['text']);
              enumerationXML.setAttribute(
                'SelectByDefault',
                newEnumerationsJSON[0]['selectByDefault'] === true ? 'true' : 'false',
              );
              newEnumerationsJSON.splice(0, 1);
            } else {
              restriction.removeChild(enumerationXML);
            }
          });

          newEnumerationsJSON.forEach((newEnumJSON: Enumeration) => {
            let newEnumElement = xml.createElementNS(ns, 'Enumeration');
            newEnumElement.setAttribute('Text', newEnumJSON['text']);
            newEnumElement.setAttribute('Value', newEnumJSON['value']);
            newEnumElement.setAttribute('SelectByDefault', newEnumJSON['selectByDefault'] === true ? 'true' : 'false');
            restriction.appendChild(newEnumElement);
          });
        }
        if (newClaims[claim].restriction === 'Pattern') {
          let currentPatternXML = Array.from(restriction.getElementsByTagName('Pattern'))[0];
          let newPatternJSON: Pattern = newClaims[claim].pattern || { regularExpression: '', helpText: '' };
          if (currentPatternXML.getAttribute('RegularExpression') !== newPatternJSON['regularExpression']) {
            currentPatternXML.setAttribute('RegularExpression', newPatternJSON['regularExpression']);
          }
          if (currentPatternXML.getAttribute('HelpText') !== newPatternJSON['helpText']) {
            currentPatternXML.setAttribute('HelpText', newPatternJSON['helpText']);
          }
        }
      }
    } else {
      let constuctedClaim = xml.createElementNS(ns, 'ClaimType');
      constuctedClaim.setAttribute('Id', newClaims[claim].id);

      let displayName = xml.createElementNS(ns, 'DisplayName');
      displayName.innerHTML = newClaims[claim].displayName ? newClaims[claim].displayName : newClaims[claim].id;
      constuctedClaim.appendChild(displayName);

      if (newClaims[claim].dataType) {
        let dataType = xml.createElementNS(ns, 'DataType');
        dataType.innerHTML = newClaims[claim].dataType;
        constuctedClaim.appendChild(dataType);
      }

      if (newClaims[claim].mask) {
        let maskXML: any = xml.createElementNS(ns, 'Mask');
        maskXML.setAttribute('Type', newClaims[claim]?.mask?.type || 'Simple');
        maskXML.setAttribute('Regex', newClaims[claim]?.mask?.regex || '');
        maskXML.innerHTML = newClaims[claim]?.mask?.value || '';
        constuctedClaim.appendChild(maskXML);
      }

      if (newClaims[claim].adminHelpText) {
        let adminHelpText: any = xml.createElementNS(ns, 'AdminHelpText');
        adminHelpText.innerHTML = newClaims[claim].adminHelpText;
        constuctedClaim.appendChild(adminHelpText);
      }

      if (newClaims[claim].userHelpText) {
        let userHelpText: any = xml.createElementNS(ns, 'UserHelpText');
        userHelpText.innerHTML = newClaims[claim].userHelpText;
        constuctedClaim.appendChild(userHelpText);
      }

      if (newClaims[claim].userInputType) {
        let userInputType = xml.createElementNS(ns, 'UserInputType');
        userInputType.innerHTML = newClaims[claim].userInputType;
        constuctedClaim.appendChild(userInputType);
      }

      if ((newClaims[claim]?.enumeration || []).length > 0) {
        let restrictionXML: Element = xml.createElementNS(ns, 'Restriction');
        for (let r of newClaims[claim].enumeration || []) {
          let enumXML: Element = xml.createElementNS(ns, 'Enumeration');
          enumXML.setAttribute('Text', r['text']);
          enumXML.setAttribute('Value', r['value']);
          enumXML.setAttribute('SelectByDefault', r['selectByDefault'] ? 'true' : 'false');
          restrictionXML.appendChild(enumXML);
        }
        constuctedClaim.appendChild(restrictionXML);
      }

      if (Object.keys(newClaims[claim]?.pattern || {}).length > 0) {
        let patternXML: Element = xml.createElementNS(ns, 'Pattern');
        patternXML.setAttribute('RegularExpression', newClaims[claim]?.pattern?.regularExpression || '');
        patternXML.setAttribute('HelpText', newClaims[claim]?.pattern?.helpText || '');
        let restrictionXML: Element = xml.createElementNS(ns, 'Restriction');
        restrictionXML.appendChild(patternXML);
        constuctedClaim.appendChild(restrictionXML);
      }

      if (constuctedClaim !== null) {
        if (xml.getElementsByTagName('ClaimsSchema').length === 0) {
          let claimsSchema = xml.createElementNS(ns, 'ClaimsSchema');
          xml.getElementsByTagName('BuildingBlocks')[0].prepend(claimsSchema);
        }
        xml.getElementsByTagName('ClaimsSchema')[0].appendChild(constuctedClaim);
      }
    }
  }
};

const upsertTransformationClaims = (
  xml: Document,
  claimTransformationElement: Element,
  transformations: Readonly<Readonly<Array<Transformation>>>,
  elementType: 'Input' | 'Output',
) => {
  const parentElement = elementType === 'Input' ? 'InputClaims' : 'OutputClaims';
  const individualElement = elementType === 'Input' ? 'InputClaim' : 'OutputClaim';
  // Add and modify input/output claims
  let modifiedClaims = transformations;
  // Upsert Input/Output Claims (if there is no InputClaims/OutputClaims element then we don't need to consider upserting and just add new ones)
  if (claimTransformationElement.getElementsByTagName(parentElement).length > 0) {
    let claimElements = Array.from(claimTransformationElement.getElementsByTagName(individualElement));

    claimElements.forEach((claimEl, index) => {
      if (index >= modifiedClaims.length) {
        removeCurrentNode(claimEl);
        return;
      }
      claimEl.setAttribute('ClaimTypeReferenceId', modifiedClaims[index].claimReferenceId);
      claimEl.setAttribute('TransformationClaimType', modifiedClaims[index].transformationClaimType);
    });

    modifiedClaims = modifiedClaims.slice(claimElements.length);
  } else {
    // Create InputClaims/OutputClaims element and insert it as the first or last child repsectively
    let newInputClaimsElement = xml.createElementNS(ns, parentElement);
    if (elementType === 'Input')
      claimTransformationElement.insertBefore(newInputClaimsElement, claimTransformationElement.firstChild);
    else claimTransformationElement.append(newInputClaimsElement);
  }

  // Create any new inputClaims/outputClaims
  modifiedClaims.forEach((mClaim) => {
    let newClaim = xml.createElementNS(ns, individualElement);
    newClaim.setAttribute('ClaimTypeReferenceId', mClaim.claimReferenceId);
    newClaim.setAttribute('TransformationClaimType', mClaim.transformationClaimType);
    claimTransformationElement.getElementsByTagName(parentElement)[0]?.appendChild(newClaim);
  });
};

const createXMLClaimsTransformations = (
  xml: Document,
  claimsTransformations: Readonly<Record<string, ClaimsTransformation>>,
  parser: DOMParser,
) => {
  if (xml.getElementsByTagName('BuildingBlocks').length > 1) {
    throw new ErrorReturn('Too many Building Blocks elements! There can only be one.');
  }
  if (xml.getElementsByTagName('BuildingBlocks').length === 0) {
    let trustFrameworkPolicyXML: any = xml.getElementsByTagName('TrustFrameworkPolicy')[0];
    let buildingBlockXML = xml.createElementNS(ns, 'BuildingBlocks');
    addXMLElem(trustFrameworkPolicyXML, buildingBlockXML, ['RelyingParty', 'UserJourneys', 'ClaimsProviders']);
  }
  let claimsTransformationsElements = xml.getElementsByTagName('ClaimsTransformations');
  let claimsTransformationsElement: Element;

  if (claimsTransformationsElements.length > 1) {
    throw new ErrorReturn('More than 1 ClaimsTransoformations element');
  } else if (claimsTransformationsElements.length === 0) {
    let buildingBlocksXML: any = xml.getElementsByTagName('BuildingBlocks')[0];
    claimsTransformationsElement = xml.createElementNS(ns, 'ClaimsTransformations');
    // if (buildingBlocks.length === 0) {
    //   throw new ErrorReturn('No Building Blocks Element');
    // } else if (buildingBlocks.length > 1) {
    //   throw new ErrorReturn('Too many Building Blocks elements! There can only be one.');
    // } else if (buildingBlocks[0].firstChild === null) {
    //   throw new ErrorReturn('No ClaimsSchema present in policy!');
    // }
    addXMLElem(buildingBlocksXML, claimsTransformationsElement, [
      'DisplayControls',
      'Localization',
      'ContentDefinitions',
      'ClientDefinitions',
    ]);
  } else {
    claimsTransformationsElement = claimsTransformationsElements[0];
  }

  let currentTransformations = Array.from(claimsTransformationsElement.getElementsByTagName('ClaimsTransformation'));

  let modifiedTransformations = Object.keys(claimsTransformations);

  currentTransformations.forEach((cT) => {
    let modifiedItemIndex = modifiedTransformations.findIndex((mT) => mT === cT.getAttribute('Id'));

    if (modifiedItemIndex === -1) {
      removeCurrentNode(cT);
      return;
    }

    // Upsert
    let currentModifiedItem = claimsTransformations[modifiedTransformations[modifiedItemIndex]];

    // Don't need to set Id since that can't have changed (thats how we match up ClaimsTransformations, if it changed we consider it a brand new transformation)
    cT.setAttribute('TransformationMethod', currentModifiedItem.transformationmethod);

    if (currentModifiedItem.inputClaims.length > 0)
      upsertTransformationClaims(xml, cT, currentModifiedItem.inputClaims, 'Input');
    else if (cT.getElementsByTagName('InputClaims').length > 0)
      removeCurrentNode(cT.getElementsByTagName('InputClaims')[0]);

    // Add and modify input parameters
    let modifiedParameters = currentModifiedItem.inputParameters;
    // Upsert InputParameters (if there is no InputParameters element then we don't need to consider upserting and just add new ones)
    if (cT.getElementsByTagName('InputParameters').length > 0) {
      let claimElements = Array.from(cT.getElementsByTagName('InputParameter'));

      claimElements.forEach((claimEl, index) => {
        if (index >= modifiedParameters.length) {
          removeCurrentNode(claimEl);
          return;
        }
        claimEl.setAttribute('Id', modifiedParameters[index].id);
        claimEl.setAttribute('DataType', modifiedParameters[index].dataType);
        claimEl.setAttribute('Value', modifiedParameters[index].value);
      });

      modifiedParameters = modifiedParameters.slice(claimElements.length);
    } else {
      // Create InputParameters element and insert it after inputClaims or as the first Child if no InputClaims
      if (
        cT.firstChild !== undefined &&
        cT.getElementsByTagName('InputClaims').length > 0 &&
        cT.getElementsByTagName('InputParameters').length > 0
      ) {
        let newInputParametersElement = xml.createElementNS(ns, 'InputParameters');
        cT.insertBefore(newInputParametersElement, cT.firstChild?.nextSibling ?? null);
      }
    }

    // Create any new inputParameters
    modifiedParameters.forEach((mParam) => {
      let newClaim = xml.createElementNS(ns, 'InputParameter');
      newClaim.setAttribute('Id', mParam.id);
      newClaim.setAttribute('DataType', mParam.dataType);
      newClaim.setAttribute('Value', mParam.value);
      cT.getElementsByTagName('InputParameters')[0]?.appendChild(newClaim);
    });

    if (currentModifiedItem.outputClaims.length > 0)
      upsertTransformationClaims(xml, cT, currentModifiedItem.outputClaims, 'Output');
    else if (cT.getElementsByTagName('OutputClaims').length > 0)
      removeCurrentNode(cT.getElementsByTagName('OutputClaims')[0]);

    modifiedTransformations.splice(modifiedItemIndex, 1);
  });

  // Add new claims transformations
  modifiedTransformations.forEach((newTransformationName) => {
    let newTransformation = claimsTransformations[newTransformationName];
    let newTransformationElement = xml.createElementNS(ns, 'ClaimsTransformation');
    newTransformationElement.setAttribute('Id', newTransformation.id);
    newTransformationElement.setAttribute('TransformationMethod', newTransformation.transformationmethod);

    if (newTransformation.inputClaims.length > 0) {
      let newTransformationInputClaims = xml.createElementNS(ns, 'InputClaims');

      newTransformation.inputClaims.forEach((newInputClaim) => {
        let newInputClaimElement = xml.createElementNS(ns, 'InputClaim');
        newInputClaimElement.setAttribute('ClaimTypeReferenceId', newInputClaim.claimReferenceId);
        newInputClaimElement.setAttribute('TransformationClaimType', newInputClaim.transformationClaimType);

        newTransformationInputClaims.appendChild(newInputClaimElement);
      });

      newTransformationElement.appendChild(newTransformationInputClaims);
    }

    if (newTransformation.inputParameters.length > 0) {
      let newTransformationInputParameters = xml.createElementNS(ns, 'InputParameters');

      newTransformation.inputParameters.forEach((newInputParam) => {
        let newInputParamElement = xml.createElementNS(ns, 'InputParameter');
        newInputParamElement.setAttribute('Id', newInputParam.id);
        newInputParamElement.setAttribute('DataType', newInputParam.dataType);
        newInputParamElement.setAttribute('Value', newInputParam.value);

        newTransformationInputParameters.appendChild(newInputParamElement);
      });

      newTransformationElement.appendChild(newTransformationInputParameters);
    }

    if (newTransformation.outputClaims.length > 0) {
      let newTransformationOutputClaims = xml.createElementNS(ns, 'OutputClaims');

      newTransformation.outputClaims.forEach((newOutputClaim) => {
        let newOutputClaimElement = xml.createElementNS(ns, 'OutputClaim');
        newOutputClaimElement.setAttribute('ClaimTypeReferenceId', newOutputClaim.claimReferenceId);
        newOutputClaimElement.setAttribute('TransformationClaimType', newOutputClaim.transformationClaimType);

        newTransformationOutputClaims.appendChild(newOutputClaimElement);
      });

      newTransformationElement.appendChild(newTransformationOutputClaims);
    }

    claimsTransformationsElement.appendChild(newTransformationElement);
  });
};

const tpInsertLocation = (profile: Element, elementToInsert: string, xml: Document) => {
  const profileOrder = [
    'Domain',
    'DisplayName',
    'Description',
    'Protocol',
    'Metadata',
    'InputTokenFormat',
    'OutputTokenFormat',
    'CryptographicKeys',
    'IncludeInSso',
    'InputClaimsTransformations',
    'InputClaims',
    'PersistedClaims',
    'DisplayClaims',
    'OutputClaims',
    'OutputClaimsTransformations',
    'ValidationTechnicalProfiles',
    'SubjectNamingInfo',
    'IncludeClaimsFromTechnicalProfile',
    'IncludeTechnicalProfile',
    'UseTechnicalProfileForSessionManagement',
    'EnabledForUserJourneys',
  ];
  const insertLocation = profileOrder.indexOf(elementToInsert);

  if (insertLocation === -1) {
    throw new ErrorReturn(
      `tpInsertLocation funciton error: ${elementToInsert} was not found in: ${profileOrder.toString()}`,
    );
  }

  let newElement = xml.createElementNS(ns, elementToInsert);

  for (let i = insertLocation - 1; i > 0; i--) {
    let insertAfter = profile.getElementsByTagName(profileOrder[i]);
    if (insertAfter.length > 0) {
      insertAfter[0].insertAdjacentElement('afterend', newElement);
      return;
    }
  }

  profile.insertBefore(newElement, profile.firstChild);
};

const addNewSelfAssertedTP = (xml: Document, selfAsserted: SelfAssertedProfile, parser: DOMParser) => {
  if (xml.getElementsByTagName('ClaimsProviders').length === 0) {
    let claimsProvidersXML = xml.createElementNS(ns, 'ClaimsProviders');
    let potentialNextTags: string[] = ['RelyingParty', 'UserJourneys', 'SubJourneys'];
    const trustFrameworkPolicy: any = xml.getElementsByTagName('TrustFrameworkPolicy');
    addXMLElem(trustFrameworkPolicy, claimsProvidersXML, potentialNextTags);
  }

  // let getParentClaimsProvider = () => {
  //   return Array.from(xml.getElementsByTagName('ClaimsProvider')).find(
  //     (cp) => cp.getElementsByTagName('DisplayName')[0]?.innerHTML === selfAsserted.claimProvider,
  //   );
  // }

  let parentClaimsProvider = getParentClaimsProvider(xml, selfAsserted.claimProvider);
  if (parentClaimsProvider === undefined) {
    addNewClaimsProvider(xml, selfAsserted.claimProvider);
    parentClaimsProvider = getParentClaimsProvider(xml, selfAsserted.claimProvider);
  }

  if (parentClaimsProvider && parentClaimsProvider.getElementsByTagName('TechnicalProfiles').length === 0) {
    let newTechnicalProfiles = xml.createElementNS(ns, 'TechnicalProfiles');
    parentClaimsProvider.appendChild(newTechnicalProfiles);
  }

  let tpElem = xml.createElementNS(ns, 'TechnicalProfile');
  tpElem.setAttribute('Id', selfAsserted.id);

  if (selfAsserted.displayName) {
    let displayNameElem = xml.createElementNS(ns, 'DisplayName');
    displayNameElem.innerHTML = selfAsserted.displayName;
    tpElem.appendChild(displayNameElem);

    // using presence of displayName here as proxy for whether TP exists in lower policies --
    // TODO: actually check lower policies
    let protocolElem = generateProtocol(xml, selfAsserted.protocol);
    tpElem.appendChild(protocolElem);
  }

  if (selfAsserted.metadata.length > 0) {
    tpElem.appendChild(generateMetadata(xml, selfAsserted.metadata));
  }
  if (selfAsserted.inputClaims.length > 0) {
    tpElem.appendChild(generateInputClaims(xml, selfAsserted.inputClaims));
  }
  if (selfAsserted.displayClaims.length > 0) {
    tpElem.appendChild(generateDisplayClaims(xml, selfAsserted.displayClaims));
  }
  if (selfAsserted.outputClaims.length > 0) {
    tpElem.appendChild(generateOutputClaims(xml, selfAsserted.outputClaims));
  }
  if (selfAsserted.inputClaimsTransformations.length > 0) {
    tpElem.appendChild(
      generateClaimsTransformations(xml, 'InputClaimsTransformation', selfAsserted.inputClaimsTransformations),
    );
  }
  if (selfAsserted.outputClaimsTransformations.length > 0) {
    tpElem.appendChild(
      generateClaimsTransformations(xml, 'OutputClaimsTransformation', selfAsserted.outputClaimsTransformations),
    );
  }
  if (selfAsserted.validationTechnicalProfiles.length > 0) {
    tpElem.appendChild(generateAllValidationTPsForSelfAsserted(xml, selfAsserted.validationTechnicalProfiles));
  }

  parentClaimsProvider?.getElementsByTagName('TechnicalProfiles')[0].appendChild(tpElem);
};

const addNewNonInteractiveTP = (xml: Document, nonInteractiveTP: NonInteractiveTechnicalProfile, parser: DOMParser) => {
  if (xml.getElementsByTagName('ClaimsProviders').length === 0) {
    let claimsProvidersXML = xml.createElementNS(ns, 'ClaimsProviders');
    let potentialNextTags: string[] = ['RelyingParty', 'UserJourneys', 'SubJourneys'];
    const trustFrameworkPolicy: any = xml.getElementsByTagName('TrustFrameworkPolicy');
    addXMLElem(trustFrameworkPolicy, claimsProvidersXML, potentialNextTags);
  }

  let parentClaimsProvider = getParentClaimsProvider(xml, nonInteractiveTP.claimProvider);
  if (parentClaimsProvider === undefined) {
    addNewClaimsProvider(xml, nonInteractiveTP.claimProvider);
    parentClaimsProvider = getParentClaimsProvider(xml, nonInteractiveTP.claimProvider);
  }

  if (parentClaimsProvider && parentClaimsProvider.getElementsByTagName('TechnicalProfiles').length === 0) {
    let newTechnicalProfiles = xml.createElementNS(ns, 'TechnicalProfiles');
    parentClaimsProvider.appendChild(newTechnicalProfiles);
  }

  // todo remove repeated code (?)
  let tpElem = xml.createElementNS(ns, 'TechnicalProfile');
  tpElem.setAttribute('Id', nonInteractiveTP.id);

  if (nonInteractiveTP.displayName) {
    let displayNameElem = xml.createElementNS(ns, 'DisplayName');
    displayNameElem.innerHTML = nonInteractiveTP.displayName;
    tpElem.appendChild(displayNameElem);

    // using presence of displayName here as proxy for whether TP exists in lower policies --
    // TODO: actually check lower policies
    let protocolElem = generateProtocol(xml, nonInteractiveTP.protocol);
    tpElem.appendChild(protocolElem);
  }

  if (nonInteractiveTP.metadata.length > 0) {
    tpElem.appendChild(generateMetadata(xml, nonInteractiveTP.metadata));
  }
  if (nonInteractiveTP.inputClaims.length > 0) {
    tpElem.appendChild(generateInputClaims(xml, nonInteractiveTP.inputClaims));
  }
  if (nonInteractiveTP['persistedClaims'] && nonInteractiveTP.persistedClaims.length > 0) {
    tpElem.appendChild(generatePersistedClaims(xml, nonInteractiveTP.persistedClaims));
  }
  if (nonInteractiveTP.outputClaims.length > 0) {
    tpElem.appendChild(generateOutputClaims(xml, nonInteractiveTP.outputClaims));
  }
  if (nonInteractiveTP.inputClaimsTransformations.length > 0) {
    tpElem.appendChild(
      generateClaimsTransformations(xml, 'InputClaimsTransformation', nonInteractiveTP.inputClaimsTransformations),
    );
  }
  if (nonInteractiveTP.outputClaimsTransformations.length > 0) {
    tpElem.appendChild(
      generateClaimsTransformations(xml, 'OutputClaimsTransformation', nonInteractiveTP.outputClaimsTransformations),
    );
  }
  if (nonInteractiveTP.includeTechnicalProfile) {
    const includeTechnicalProfileXML = xml.createElementNS(ns, 'IncludeTechnicalProfile');
    includeTechnicalProfileXML.setAttribute('ReferenceId', nonInteractiveTP.includeTechnicalProfile);
    tpElem.appendChild(includeTechnicalProfileXML);
  }
  parentClaimsProvider?.getElementsByTagName('TechnicalProfiles')[0].appendChild(tpElem);
};

const insertOptionalTextContentTP = (
  tagName: string,
  item: 'domain' | 'description',
  currentTP: Element,
  newTP: Readonly<CommonTechnicalProfile>,
  xml: Document,
) => {
  if (currentTP.querySelector(tagName) !== null) {
    let currentNode = currentTP.querySelector(tagName)!;
    if (newTP[item] !== '') currentNode.innerHTML = newTP[item];
    else removeCurrentNode(currentNode);
  } else if (newTP[item] !== '') {
    tpInsertLocation(currentTP, tagName, xml);
    currentTP.querySelector(tagName)!.innerHTML = newTP[item];
  }
};

const insertRequiredTextContentTP = (
  tagName: string,
  item: 'displayName',
  currentTP: Element,
  newTP: Readonly<CommonTechnicalProfile>,
  xml: Document,
) => {
  if (newTP[item] !== '') {
    if (currentTP.querySelector(tagName) === null) tpInsertLocation(currentTP, tagName, xml);
    currentTP.querySelector(tagName)!.innerHTML = newTP[item];
  }
};

const removeCurrentNode = (currentNode: Node | null) => {
  if (currentNode === null) return;

  currentNode.parentNode?.removeChild(currentNode);
};

const upsertValidationTP = (xml: Document, element: Element, validationTP: ValidationTP, parser: DOMParser) => {
  element.setAttribute('ReferenceId', validationTP.id);

  if (validationTP.continueOnError) element.setAttribute('ContinueOnError', 'true');
  else element.removeAttribute('ContinueOnError');

  if (!validationTP.continueOnSuccess) element.setAttribute('ContinueOnSuccess', 'false');
  else element.removeAttribute('ContinueOnSuccess');

  if (validationTP.preconditions.length === 0 && element.getElementsByTagName('Preconditions').length > 0) {
    let preconditions = element.getElementsByTagName('Preconditions')[0];

    removeCurrentNode(preconditions);
  } else if (validationTP.preconditions.length !== 0) {
    if (element.getElementsByTagName('Preconditions').length === 0) {
      let preconditionsElement = xml.createElementNS(ns, 'Preconditions');
      if (preconditionsElement.firstChild === null)
        throw new ErrorReturn('Problem creating a Preconditions element for ' + validationTP.id);
      element.appendChild(preconditionsElement.firstChild);
    }

    let preconditionsToUpsert = [...validationTP.preconditions];
    let currentPreconditionElements = element.getElementsByTagName('Precondition');

    // Preconditions are weird, since there are not real unique identifiers and order liekly wants to be prreseved this upsert is only really here to preserve any comments
    Array.from(currentPreconditionElements).forEach((preconditionElement, index) => {
      if (index >= currentPreconditionElements.length) {
        removeCurrentNode(preconditionElement);
        return;
      }

      let currentPreconditonToUpsert = preconditionsToUpsert[index];

      preconditionElement.setAttribute('Type', preconditionTypeToString(currentPreconditonToUpsert.type));
      preconditionElement.setAttribute(
        'ExecuteActionsIf',
        currentPreconditonToUpsert.executeActionsIf ? 'true' : 'false',
      );

      // In current iteration of B2C custom policy this shouuld always be true for Validation Technical profile actions
      if (currentPreconditonToUpsert.action === PreconditionAction.SKIPVALIDATIONTECHINCALPROFILE) {
        if (preconditionElement.getElementsByTagName('Action').length === 0) {
          let actionTag = xml.createElementNS(ns, 'Action').firstChild;

          if (actionTag === null) {
            throw new ErrorReturn(
              'Problem creating an Action element for Precondition #' + index + ', in validationTP: ' + validationTP.id,
            );
          }
          actionTag.textContent = currentPreconditonToUpsert.action;
          preconditionElement.appendChild(actionTag);
        } else {
          preconditionElement.getElementsByTagName('Action')[0].textContent = currentPreconditonToUpsert.action;
        }
      } else {
        throw new ErrorReturn(
          'Invalid action type: ' +
            currentPreconditonToUpsert.action +
            ', in validation technical profile: ' +
            validationTP.id,
        );
      }

      let values = [...currentPreconditonToUpsert.values];
      let preconditionValues = preconditionElement.getElementsByTagName('Value');

      Array.from(preconditionValues).forEach((val, ind) => {
        if (ind >= values.length) {
          removeCurrentNode(val);
          return;
        }

        val.textContent = values[ind];
      });

      for (let newValsIndex = preconditionValues.length; newValsIndex < values.length; newValsIndex++) {
        let val = xml.createElementNS(ns, 'Value').firstElementChild;

        let currentValues = preconditionElement.getElementsByTagName('Value');

        if (val === null) {
          throw new ErrorReturn(
            'Problem creating an Value element for Precondition #' + index + ', in validationTP: ' + validationTP.id,
          );
        }
        val.textContent = values[newValsIndex];
        currentValues[currentValues.length - 1].insertAdjacentElement('afterend', val);
      }
    });

    for (
      let newPreconIndex = currentPreconditionElements.length;
      newPreconIndex < validationTP.preconditions.length;
      newPreconIndex++
    ) {
      let precondition = validationTP.preconditions[newPreconIndex];
      let preconType = preconditionTypeToString(precondition.type);

      let tempPreconditionString = `<Precondition Type="${preconType}" ExecuteActionsIf="${precondition.executeActionsIf}">`;

      precondition.values.forEach((value) => {
        tempPreconditionString += `<Value>${value}</Value>`;
      });

      tempPreconditionString += `<Action>SkipThisValidationTechnicalProfile</Action>`;

      tempPreconditionString += '</Precondition>';

      let parsedPrecondition = parser.parseFromString(tempPreconditionString, 'text/xml').firstElementChild;
      if (parsedPrecondition === null) {
        throw new ErrorReturn(
          'Problem creating Precondition #' + newPreconIndex + ', in validationTP: ' + validationTP.id,
        );
      }

      element.getElementsByTagName('Preconditions')[0].appendChild(parsedPrecondition);
    }
  }
};

const upsertCommonTP = (xml: Document, currentTP: Element, newTP: Readonly<CommonTechnicalProfile>) => {
  currentTP.setAttribute('Id', newTP.id);

  insertRequiredTextContentTP('DisplayName', 'displayName', currentTP, newTP, xml);
  insertOptionalTextContentTP('Domain', 'domain', currentTP, newTP, xml);
  insertOptionalTextContentTP('Description', 'description', currentTP, newTP, xml);

  if (newTP.protocol[0] !== '') {
    if (currentTP.querySelector('Protocol') === null) {
      tpInsertLocation(currentTP, 'Protocol', xml);
    }
    currentTP.querySelector('Protocol')!.setAttribute('Name', newTP.protocol[0]);

    if (newTP.protocol[1] !== '') {
      currentTP.querySelector('Protocol')!.setAttribute('Handler', newTP.protocol[1]);
    } else {
      currentTP.querySelector('Protocol')!.removeAttribute('Handler');
    }
  } else if (currentTP.querySelector('Protocol') !== null) {
    removeCurrentNode(currentTP.querySelector('Protocol'));
  }

  if (newTP.metadata.length > 0) {
    if (currentTP.querySelector('Metadata') === null) {
      tpInsertLocation(currentTP, 'Metadata', xml);
    }

    // Iterate thorugh items adding, removing, and updating as needed
    let items = Array.from(currentTP.getElementsByTagName('Item')) ?? [];
    let unusedNewItems = [...newTP.metadata];

    items.forEach((element) => {
      let modifiedItem = unusedNewItems.findIndex((elm) => elm[0] === element.getAttribute('Key'));

      if (modifiedItem !== -1) {
        element.setAttribute('Key', unusedNewItems[modifiedItem][0]);
        element.innerHTML = unusedNewItems[modifiedItem][1];
        unusedNewItems.splice(modifiedItem, 1);
      } else {
        removeCurrentNode(element);
      }
    });

    // The rest are new items so we just create new elements and append them
    unusedNewItems.forEach((newMetadata) => {
      let newItem = xml.createElementNS(ns, 'Item');
      newItem.setAttribute('Key', newMetadata[0]);
      newItem.innerHTML = newMetadata[1];

      currentTP.querySelector('Metadata')?.appendChild(newItem);
    });
  } else if (currentTP.querySelector('Metadata') !== null) {
    removeCurrentNode(currentTP.querySelector('Metadata')!);
  } // End of add meta data

  if (newTP.inputClaimsTransformations.length > 0) {
    if (currentTP.querySelector('InputClaimsTransformations') === null) {
      tpInsertLocation(currentTP, 'InputClaimsTransformations', xml);
    }

    // Iterate thorugh items adding, removing, and updating as needed
    let items = Array.from(currentTP.getElementsByTagName('InputClaimsTransformation')) ?? [];
    let unusedNewItems = [...newTP.inputClaimsTransformations];

    items.forEach((element) => {
      let modifiedItem = unusedNewItems.findIndex((elm) => elm === element.getAttribute('ReferenceId'));

      if (modifiedItem !== -1) {
        element.setAttribute('ReferenceId', unusedNewItems[modifiedItem]);
        unusedNewItems.splice(modifiedItem, 1);
      } else {
        removeCurrentNode(element);
      }
    });

    // The rest are new items so we just create new elements and append them
    unusedNewItems.forEach((newInputTransformation) => {
      let newItem = xml.createElementNS(ns, 'InputClaimsTransformation');
      newItem.setAttribute('ReferenceId', newInputTransformation);

      currentTP.querySelector('InputClaimsTransformations')?.appendChild(newItem);
    });
  } else if (currentTP.querySelector('InputClaimsTransformations') !== null) {
    currentTP.removeChild(currentTP.querySelector('InputClaimsTransformations')!);
  } // End of Input ClaimsTransformations

  if (newTP.inputClaims.length > 0) {
    if (currentTP.querySelector('InputClaims') === null) {
      tpInsertLocation(currentTP, 'InputClaims', xml);
    }

    // Iterate thorugh items adding, removing, and updating as needed
    let items = Array.from(currentTP.getElementsByTagName('InputClaim')) ?? [];
    let unusedNewItems = [...newTP.inputClaims];

    items.forEach((element) => {
      let modifiedItem = unusedNewItems.findIndex(
        (elm) => elm.claimTypeReferenceId === element.getAttribute('ClaimTypeReferenceId'),
      );

      if (modifiedItem !== -1) {
        element.setAttribute('ClaimTypeReferenceId', unusedNewItems[modifiedItem].claimTypeReferenceId);

        if (unusedNewItems[modifiedItem].defaultValue === '') {
          element.removeAttribute('DefaultValue');
        } else {
          element.setAttribute('DefaultValue', unusedNewItems[modifiedItem].defaultValue);
        }

        if (unusedNewItems[modifiedItem].partnerClaimType === '') {
          element.removeAttribute('PartnerClaimType');
        } else {
          element.setAttribute('PartnerClaimType', unusedNewItems[modifiedItem].partnerClaimType);
        }

        if (unusedNewItems[modifiedItem].required === false) {
          element.removeAttribute('Required');
        } else {
          element.setAttribute('Required', 'true');
        }

        if (unusedNewItems[modifiedItem].alwaysUseDefaultValue === false) {
          element.removeAttribute('AlwaysUseDefaultValue');
        } else {
          element.setAttribute('AlwaysUseDefaultValue', 'true');
        }
        unusedNewItems.splice(modifiedItem, 1);
      } else {
        removeCurrentNode(element);
      }
    });

    // The rest are new items so we just create new elements and append them
    unusedNewItems.forEach((newInputClaims) => {
      let newItem = xml.createElementNS(ns, 'InputClaim');
      newItem.setAttribute('ClaimTypeReferenceId', newInputClaims.claimTypeReferenceId);

      if (newInputClaims.defaultValue !== '') {
        newItem.setAttribute('DefaultValue', newInputClaims.defaultValue);
      }

      if (newInputClaims.partnerClaimType !== '') {
        newItem.setAttribute('PartnerClaimType', newInputClaims.partnerClaimType);
      }

      if (newInputClaims.required === true) {
        newItem.setAttribute('Required', 'true');
      }

      if (newInputClaims.alwaysUseDefaultValue === true) {
        newItem.setAttribute('AlwaysUseDefaultValue', 'true');
      }

      currentTP.querySelector('InputClaims')?.appendChild(newItem);
    });
  } else if (currentTP.querySelector('InputClaims') !== null) {
    currentTP.removeChild(currentTP.querySelector('InputClaims')!);
  } // end of Input Claims upsert

  if (newTP.outputClaims.length > 0) {
    if (currentTP.querySelector('OutputClaims') === null) {
      tpInsertLocation(currentTP, 'OutputClaims', xml);
    }

    // Iterate thorugh items adding, removing, and updating as needed
    let items = Array.from(currentTP.getElementsByTagName('OutputClaim')) ?? [];
    let unusedNewItems = [...newTP.outputClaims];

    items.forEach((element) => {
      let modifiedItem = unusedNewItems.findIndex(
        (elm) => elm.claimTypeReferenceId === element.getAttribute('ClaimTypeReferenceId'),
      );

      if (modifiedItem !== -1) {
        element.setAttribute('ClaimTypeReferenceId', unusedNewItems[modifiedItem].claimTypeReferenceId);

        if (unusedNewItems[modifiedItem].defaultValue === '') {
          element.removeAttribute('DefaultValue');
        } else {
          element.setAttribute('DefaultValue', unusedNewItems[modifiedItem].defaultValue);
        }

        if (unusedNewItems[modifiedItem].partnerClaimType === '') {
          element.removeAttribute('PartnerClaimType');
        } else {
          element.setAttribute('PartnerClaimType', unusedNewItems[modifiedItem].partnerClaimType);
        }

        if (unusedNewItems[modifiedItem].required === false) {
          element.removeAttribute('Required');
        } else {
          element.setAttribute('Required', 'true');
        }

        if (unusedNewItems[modifiedItem].alwaysUseDefaultValue === false) {
          element.removeAttribute('AlwaysUseDefaultValue');
        } else {
          element.setAttribute('AlwaysUseDefaultValue', 'true');
        }
        unusedNewItems.splice(modifiedItem, 1);
      } else {
        removeCurrentNode(element);
      }
    });

    // The rest are new items so we just create new elements and append them
    unusedNewItems.forEach((newOutputClaims) => {
      let newItem = xml.createElementNS(ns, 'OutputClaim');
      newItem.setAttribute('ClaimTypeReferenceId', newOutputClaims.claimTypeReferenceId);

      if (newOutputClaims.defaultValue !== '') {
        newItem.setAttribute('DefaultValue', newOutputClaims.defaultValue);
      }

      if (newOutputClaims.partnerClaimType !== '') {
        newItem.setAttribute('PartnerClaimType', newOutputClaims.partnerClaimType);
      }

      if (newOutputClaims.required === true) {
        newItem.setAttribute('Required', 'true');
      }

      if (newOutputClaims.alwaysUseDefaultValue === true) {
        newItem.setAttribute('AlwaysUseDefaultValue', 'true');
      }

      currentTP.querySelector('OutputClaims')?.appendChild(newItem);
    });
  } else if (currentTP.querySelector('OutputClaims') !== null) {
    currentTP.removeChild(currentTP.querySelector('OutputClaims')!);
  } // End of Output Claims upsert

  if (newTP.outputClaimsTransformations.length > 0) {
    if (currentTP.querySelector('OutputClaimsTransformations') === null) {
      tpInsertLocation(currentTP, 'OutputClaimsTransformations', xml);
    }

    // Iterate thorugh items adding, removing, and updating as needed
    let items = Array.from(currentTP.getElementsByTagName('OutputClaimsTransformation')) ?? [];
    let unusedNewItems = [...newTP.outputClaimsTransformations];

    items.forEach((element) => {
      let modifiedItem = unusedNewItems.findIndex((elm) => elm === element.getAttribute('ReferenceId'));

      if (modifiedItem !== -1) {
        element.setAttribute('ReferenceId', unusedNewItems[modifiedItem]);
        unusedNewItems.splice(modifiedItem, 1);
      } else {
        removeCurrentNode(element);
      }
    });

    // The rest are new items so we just create new elements and append them
    unusedNewItems.forEach((newOutputTransformation) => {
      let newItem = xml.createElementNS(ns, 'OutputClaimsTransformation');
      newItem.setAttribute('ReferenceId', newOutputTransformation);

      currentTP.querySelector('OutputClaimsTransformations')?.appendChild(newItem);
    });
  } else if (currentTP.querySelector('OutputClaimsTransformations') !== null) {
    currentTP.removeChild(currentTP.querySelector('OutputClaimsTransformations')!);
  }
};

const upsertSelfAssertedTP = (
  xml: Document,
  currentTP: Element,
  newTP: Readonly<SelfAssertedProfile>,
  parser: DOMParser,
) => {
  upsertCommonTP(xml, currentTP, newTP);

  if (newTP.displayClaims.length > 0) {
    if (currentTP.querySelector('DisplayClaims') === null) {
      tpInsertLocation(currentTP, 'DisplayClaims', xml);
    }

    // Iterate thorugh items adding, removing, and updating as needed
    let items = Array.from(currentTP.getElementsByTagName('DisplayClaim')) ?? [];
    let unusedNewItems = [...newTP.displayClaims];

    items.forEach((element) => {
      let modifiedItem = unusedNewItems.findIndex(
        (elm) =>
          elm.claimTypeReferenceId === element.getAttribute('ClaimTypeReferenceId') ||
          elm.displayControlReferenceId === element.getAttribute('DisplayControlReferenceId'),
      );

      if (modifiedItem !== -1) {
        if (unusedNewItems[modifiedItem].claimTypeReferenceId !== '')
          element.setAttribute('ClaimTypeReferenceId', unusedNewItems[modifiedItem].claimTypeReferenceId);
        else element.removeAttribute('ClaimTypeReferenceId');

        if (unusedNewItems[modifiedItem].displayControlReferenceId !== '')
          element.setAttribute('DisplayControlReferenceId', unusedNewItems[modifiedItem].displayControlReferenceId);
        else element.removeAttribute('DisplayControlReferenceId');

        if (unusedNewItems[modifiedItem].required === false) {
          element.removeAttribute('Required');
        } else {
          element.setAttribute('Required', 'true');
        }

        unusedNewItems.splice(modifiedItem, 1);
      } else {
        removeCurrentNode(element);
      }
    });

    // The rest are new items so we just create new elements and append them
    unusedNewItems.forEach((newDisplayClaims) => {
      let newItem = xml.createElementNS(ns, 'DisplayClaim');
      if (newDisplayClaims.claimTypeReferenceId !== '')
        newItem.setAttribute('ClaimTypeReferenceId', newDisplayClaims.claimTypeReferenceId);

      if (newDisplayClaims.displayControlReferenceId !== '')
        newItem.setAttribute('DisplayControlReferenceId', newDisplayClaims.claimTypeReferenceId);

      if (newDisplayClaims.required === true) {
        newItem.setAttribute('Required', 'true');
      }

      currentTP.querySelector('DisplayClaims')?.appendChild(newItem);
    });
  } else if (currentTP.querySelector('DisplayClaims') !== null) {
    currentTP.removeChild(currentTP.querySelector('DisplayClaims')!);
  } // End of Display Claims upsert

  if (newTP.validationTechnicalProfiles.length > 0) {
    if (currentTP.querySelector('ValidationTechnicalProfiles') === null) {
      tpInsertLocation(currentTP, 'ValidationTechnicalProfiles', xml);
    }

    // Iterate thorugh items adding, removing, and updating as needed
    let items = Array.from(currentTP.getElementsByTagName('ValidationTechnicalProfile'));
    let unusedNewItems = [...newTP.validationTechnicalProfiles];

    items.forEach((element) => {
      let modifiedItem = unusedNewItems.findIndex((elm) => elm.id === element.getAttribute('ReferenceId'));

      if (modifiedItem !== -1) {
        upsertValidationTP(xml, element, unusedNewItems[modifiedItem], parser);
        unusedNewItems.splice(modifiedItem, 1);
      } else {
        removeCurrentNode(element);
      }
    });

    // The rest are new items so we just create new elements and append them
    unusedNewItems.forEach((newValidation) => {
      let newItem = generateSingleValidationTP(xml, newValidation);
      currentTP.querySelector('ValidationTechnicalProfiles')?.appendChild(newItem);
    });
  } else if (currentTP.querySelector('ValidationTechnicalProfiles') !== null) {
    currentTP.removeChild(currentTP.querySelector('ValidationTechnicalProfiles')!);
  }
};

const upsertOtherTP = (xml: Document, currentTP: Element, newTP: Readonly<NonInteractiveTechnicalProfile>) => {
  upsertCommonTP(xml, currentTP, newTP);

  if (newTP.persistedClaims.length > 0) {
    if (currentTP.querySelector('PersistedClaims') === null) {
      tpInsertLocation(currentTP, 'PersistedClaims', xml);
    }

    // Iterate thorugh items adding, removing, and updating as needed
    let items = Array.from(currentTP.getElementsByTagName('PersistedClaim')) ?? [];
    let unusedNewItems = [...newTP.persistedClaims];

    items.forEach((element) => {
      let modifiedItem = unusedNewItems.findIndex(
        (elm) => elm.claimTypeReferenceId === element.getAttribute('ClaimTypeReferenceId'),
      );

      if (modifiedItem !== -1) {
        element.setAttribute('ClaimTypeReferenceId', unusedNewItems[modifiedItem].claimTypeReferenceId);

        if (unusedNewItems[modifiedItem].partnerClaimType !== '')
          element.setAttribute('PartnerClaimType', unusedNewItems[modifiedItem].partnerClaimType);
        else element.removeAttribute('PartnerClaimType');

        if (unusedNewItems[modifiedItem].defaultValue !== '')
          element.setAttribute('DefaultValue', unusedNewItems[modifiedItem].defaultValue);
        else element.removeAttribute('DefaultValue');

        unusedNewItems.splice(modifiedItem, 1);
      } else {
        removeCurrentNode(element);
      }
    });

    // The rest are new items so we just create new elements and append them
    unusedNewItems.forEach((newDisplayClaims) => {
      let newItem = xml.createElementNS(ns, 'PersistedClaim');
      newItem.setAttribute('ClaimTypeReferenceId', newDisplayClaims.claimTypeReferenceId);

      if (newDisplayClaims.partnerClaimType !== '')
        newItem.setAttribute('PartnerClaimType', newDisplayClaims.partnerClaimType);

      if (newDisplayClaims.defaultValue !== '') newItem.setAttribute('DefaultValue', newDisplayClaims.defaultValue);

      currentTP.querySelector('PersistedClaims')?.appendChild(newItem);
    });
  } else if (currentTP.querySelector('PersistedClaims') !== null) {
    currentTP.removeChild(currentTP.querySelector('PersistedClaims')!);
  } // End of Persisted Claims upsert
};

const createXMLTechnicalProfiles = (
  xml: Document,
  selfAsserted: Readonly<Record<string, SelfAssertedProfile>>,
  other: Readonly<Record<string, NonInteractiveTechnicalProfile>>,
  parser: DOMParser,
) => {
  const currentXMLTP = Array.from(xml.querySelectorAll('TechnicalProfiles>TechnicalProfile'));

  // Get the names of each profile so that we can know which ones are new
  let allSelfAssertedTP = Object.keys(selfAsserted);
  let allOtherTP = Object.keys(other);

  currentXMLTP.forEach((tp) => {
    let tpId = tp.getAttribute('Id') ?? '';
    if (selfAsserted[tpId] !== undefined) {
      // modify self asserted profile and remove id from allSelfAssertedTP
      upsertSelfAssertedTP(xml, tp, selfAsserted[tpId], parser);
      allSelfAssertedTP = allSelfAssertedTP.filter((selfAssertedId) => selfAssertedId !== tpId);
    } else if (other[tpId] !== undefined) {
      // modify other profile and remove id from allOtherTP
      upsertOtherTP(xml, tp, other[tpId]);
      allOtherTP = allOtherTP.filter((otherTpId) => otherTpId !== tpId);
    } else {
      // remove profile
      removeCurrentNode(tp);
    }
  });

  for (let selfAssertedName of allSelfAssertedTP) {
    addNewSelfAssertedTP(xml, selfAsserted[selfAssertedName], parser);
  }
  for (let otherTpName of allOtherTP) {
    addNewNonInteractiveTP(xml, other[otherTpName], parser);
  }
};

const addXMLElem = (parent: Document, xmlElement: Element, potentialNextTags: string[]): void => {
  let nextTagName: string | undefined;
  potentialNextTags.forEach((potentialNextTag) => {
    if (parent.getElementsByTagName(potentialNextTag).length > 0) {
      nextTagName = potentialNextTag;
    }
  });
  if (nextTagName) {
    let nextTag = parent.getElementsByTagName(nextTagName)[0];
    parent.insertBefore(xmlElement, nextTag);
  } else {
    parent.appendChild(xmlElement);
  }
};

const upsertPrecondition = (xml: Document, precXML: any, precJSON: Precondition) => {
  precJSON['type'] &&
    precXML.getAttribute('Type') !== precJSON['type'] &&
    precXML.setAttribute('Type', precJSON['type']);

  precXML.getAttribute('ExecuteActionsIf') != precJSON['executeActionsIf'].toString() &&
    precXML.setAttribute('ExecuteActionsIf', precJSON['executeActionsIf'].toString());

  if (precXML.querySelectorAll('Value').length === 0) {
    const valueXML = xml.createElementNS(ns, 'Value');
    precXML.appendChild(valueXML);
  }
  if (precXML.querySelectorAll('Value').length === precJSON['values'].length) {
    // update Values
    Array.from(precXML.getElementsByTagName('Value')).forEach((value: any, _index: number) => {
      if (value.innerHTML !== precJSON['values'][_index]) value.innerHTML = precJSON['values'][_index];
    });
  } else if (precXML.querySelectorAll('Value').length > precJSON['values'].length) {
    const _valuesXML = precXML.getElementsByTagName('Value');
    if (_valuesXML[0].innerHTML != precJSON['values'][0]) _valuesXML[0].innerHTML = precJSON['values'][0];

    // delete second value
    let y: Element = precXML.getElementsByTagName('Value')[1];
    precXML.removeChild(y);
  } else {
    const _valuesXML = precXML.getElementsByTagName('Value');
    if (_valuesXML[0].innerHTML != precJSON['values'][0]) _valuesXML[0].innerHTML = precJSON['values'][0];
    // add second value
    const valueXML = xml.createElementNS(ns, 'Value');
    valueXML.innerHTML = precJSON['values'][1];
    addXMLElem(precXML, valueXML, ['Action']);
  }
  if (precXML.querySelectorAll('Action').length === 0) {
    const actionXML = xml.createElementNS(ns, 'Action');
    precXML.appendChild(actionXML);
  }
  if (precJSON['action'] && precXML.getElementsByTagName('Action')[0].innerHTML !== precJSON['action'])
    precXML.getElementsByTagName('Action')[0].innerHTML = precJSON['action'];
};
const upsertJourney = (
  xml: Document,
  journey: UserJourney | SubJourney,
  parser: DOMParser,
  journeyType: 'UserJourney' | 'SubJourney',
) => {
  let xmlJourney: any = xml.querySelector(journeyType + 's>' + journeyType + "[Id='" + journey['id'] + "']");
  // update journey attributes
  if (journeyType === 'UserJourney') {
    // @ts-ignore
    journey.defaultCpimIssuerTechnicalProfileReferenceId &&
      xmlJourney.setAttribute(
        'DefaultCpimIssuerTechnicalProfileReferenceId',
        // @ts-ignore
        journey.defaultCpimIssuerTechnicalProfileReferenceId,
      );
    // update userJourney element - AuthorizationTechnicalProfile
    // @ts-ignore
    journey.authorizationTP &&
      // @ts-ignore
      xmlJourney.querySelector('AuthorizationTechnicalProfile').setAttribute('ReferenceId', journey.authorizationTP);
  }
  if (journeyType === 'SubJourney') {
    // @ts-ignore
    journey.type && xmlJourney.setAttribute('Type', journey.type);
  }
  let orchStepsXML: any;
  // Create OrchestrationSteps Element if it does not exist
  if (!xmlJourney.querySelector('OrchestrationSteps') && journey['orchestrationSteps'].length > 0) {
    orchStepsXML = xml.createElementNS(ns, 'OrchestrationSteps');
    xmlJourney.appendChild(orchStepsXML);
  }
  // update journey element - OrchestrationSteps
  const allOrchStepsXML: any = Array.from(xmlJourney.querySelectorAll('OrchestrationSteps>OrchestrationStep'));
  let j = 0;

  const _jsonSteps = journey['orchestrationSteps'].reduce((acc: any, step: OrchestrationStep) => {
    const jsonStepName =
      step['claimsExchanges'].length > 0
        ? step['claimsExchanges'][0]['id']
        : step['cpimIssuerTechnicalProfileReferenceId'];
    return { ...acc, [jsonStepName]: step };
  }, {});

  // console.log('jsonSteps :', _jsonSteps);

  allOrchStepsXML.forEach((orchStepXML: any, index: number) => {
    const claimsExchangesXML = orchStepXML.querySelectorAll('ClaimsExchanges>ClaimsExchange');
    let xmlStepName: string =
      claimsExchangesXML && claimsExchangesXML.length > 0
        ? claimsExchangesXML[0].getAttribute('Id') || ''
        : orchStepXML.getAttribute('CpimIssuerTechnicalProfileReferenceId') || '';

    if (_jsonSteps.hasOwnProperty(xmlStepName)) {
      // update the orchestrationstep
      let orchStepJSON: OrchestrationStep = _jsonSteps[xmlStepName];
      delete _jsonSteps[xmlStepName];
      // update/set the orchestration step attributes
      orchStepJSON['order'] &&
        orchStepJSON['order'].toString() != orchStepXML.getAttribute('Order') &&
        orchStepXML.setAttribute('Order', orchStepJSON['order'].toString());

      orchStepJSON.type &&
        orchStepJSON['type'] != orchStepXML.getAttribute('Type') &&
        orchStepXML.setAttribute('Type', orchStepJSON['type']);

      orchStepJSON.contentDefinitonReferenceId &&
        orchStepJSON['contentDefinitonReferenceId'] != orchStepXML.getAttribute('ContentDefinitionReferenceId') &&
        orchStepXML.setAttribute('ContentDefinitionReferenceId', orchStepJSON['contentDefinitonReferenceId']);

      orchStepJSON.cpimIssuerTechnicalProfileReferenceId &&
        orchStepJSON['cpimIssuerTechnicalProfileReferenceId'] !=
          orchStepXML.getAttribute('CpimIssuerTechnicalProfileReferenceId') &&
        orchStepXML.setAttribute(
          'CpimIssuer TechnicalProfileReferenceId',
          orchStepJSON['cpimIssuerTechnicalProfileReferenceId'],
        );

      // update preconditions
      if (orchStepJSON['preconditions'].length > 0) {
        // add / update preconditions
        let preconditionsXML: any;
        if (orchStepXML.getElementsByTagName('Preconditions').length === 0) {
          preconditionsXML = xml.createElementNS(ns, 'Preconditions');
          addXMLElem(orchStepXML, preconditionsXML, ['ClaimsProviderSelections', 'ClaimsExchanges']);
        }
        preconditionsXML = orchStepXML.getElementsByTagName('Preconditions');
        // update/set precondition Selection
        const allPreconditionXML: any = Array.from(orchStepXML.querySelectorAll('Preconditions>Precondition'));
        let preconditionsJSON: Precondition[] = cloneDeep(orchStepJSON['preconditions']);
        allPreconditionXML.forEach((preconditionXML: any) => {
          if (preconditionsJSON.length === 0) {
            orchStepXML.removeChild(preconditionXML);
          } else {
            const _precJSON = preconditionsJSON[0];
            upsertPrecondition(xml, preconditionXML, _precJSON);
            preconditionsJSON.splice(0, 1);
          }
        });
        // Add new precondition
        if (preconditionsJSON.length > 0) {
          preconditionsJSON.forEach((precondition: Precondition) => {
            const precXML = xml.createElementNS(ns, 'Precondition');
            upsertPrecondition(xml, precXML, precondition);
            preconditionsXML[0].appendChild(precXML);
          });
        }
      } else if (
        orchStepXML.querySelectorAll('Preconditions>Precondition').length > 0 &&
        orchStepJSON['preconditions'].length == 0
      ) {
        // delete the Preconditions if it exists
        orchStepXML.removeChild(orchStepXML.getElementsByTagName('Preconditions')[0]);
      } else {
        // No preconditions in XML and no data in JSON
        // console.log('do nothing in preconditions');
      }

      // update claimsProviderSelections attributes
      let claimsProviderSelectionsXML: any;
      if (
        orchStepJSON['claimsProviderSelections']['displayOptions'] ||
        orchStepJSON['claimsProviderSelections']['providers'].length > 0
      ) {
        // create or update claimsProviderSelections
        if (orchStepXML.getElementsByTagName('ClaimsProviderSelections').length === 0) {
          claimsProviderSelectionsXML = xml.createElementNS(ns, 'ClaimsProviderSelections');
          addXMLElem(orchStepXML, claimsProviderSelectionsXML, ['ClaimsExchanges']);
        }
        claimsProviderSelectionsXML = orchStepXML.getElementsByTagName('ClaimsProviderSelections');
        orchStepJSON['claimsProviderSelections']['displayOptions'] &&
          claimsProviderSelectionsXML[0].getAttribute('DisplayOption') !==
            orchStepJSON['claimsProviderSelections']['displayOptions'] &&
          claimsProviderSelectionsXML[0].setAttribute(
            'DisplayOption',
            orchStepJSON['claimsProviderSelections']['displayOptions'],
          );
        // update/set claimProvider Selection
        const allClaimsProvidersXML: any = Array.from(
          orchStepXML.querySelectorAll('ClaimsProviderSelections>ClaimsProviderSelection'),
        );

        let claimsProvidersJSON = cloneDeep(orchStepJSON['claimsProviderSelections']);

        allClaimsProvidersXML.forEach((claimsProviderXML: any) => {
          if (claimsProvidersJSON['providers'].length === 0) {
            orchStepXML.removeChild(claimsProviderXML);
          } else {
            const claimProviderJSON = claimsProvidersJSON['providers'][0];
            claimProviderJSON['targetClaimExchangeId'] &&
              claimsProviderXML.getAttribute('TargetClaimsExchangeId') !== claimProviderJSON['targetClaimExchangeId'] &&
              claimsProviderXML.setAttribute('TargetClaimsExchangeId', claimProviderJSON['targetClaimExchangeId']);

            claimProviderJSON['validationClaimsExchange'] &&
              claimsProviderXML.getAttribute('ValidationClaimsExchangeId') !==
                claimProviderJSON['validationClaimsExchange'] &&
              claimsProviderXML.setAttribute(
                'ValidationClaimsExchangeId',
                claimProviderJSON['validationClaimsExchange'],
              );

            claimsProvidersJSON['providers'].splice(0, 1);
          }
        });
        // Add new claims provider
        if (claimsProvidersJSON['providers'].length > 0) {
          // add claimProvider Selection elements
          claimsProvidersJSON['providers'].forEach((provider: ProviderSelect) => {
            let claimProviderXML = xml.createElementNS(ns, 'ClaimsProviderSelection');
            provider['targetClaimExchangeId'] &&
              claimProviderXML.setAttribute('TargetClaimsExchangeId', provider['targetClaimExchangeId']);
            provider['validationClaimsExchange'] &&
              claimProviderXML.setAttribute('ValidationClaimsExchangeId', provider['validationClaimsExchange']);
            claimsProviderSelectionsXML[0].appendChild(claimProviderXML);
          });
        }
      } else if (
        orchStepXML.querySelectorAll('ClaimsProviderSelections>ClaimsProviderSelection').length > 0 &&
        !orchStepJSON['claimsProviderSelections']['displayOptions'] &&
        orchStepJSON['claimsProviderSelections']['providers'].length == 0
      ) {
        // delete the claimsProviderSelections if it exists
        orchStepXML.removeChild(orchStepXML.getElementsByTagName('ClaimsProviderSelections')[0]);
      } else {
        // No claimsProviderSelections in XML and no data in JSON
        // console.log('do nothing in claimsProviderSelections');
      }

      // update/set claimExchanges
      let _claimsExchangesXML: any;
      if (orchStepJSON['claimsExchanges'].length > 0) {
        // create or update claimsExchanges
        if (orchStepXML.getElementsByTagName('ClaimsExchanges').length === 0) {
          _claimsExchangesXML = xml.createElementNS(ns, 'ClaimsExchanges');
          addXMLElem(orchStepXML, _claimsExchangesXML, []);
        }
        _claimsExchangesXML = orchStepXML.getElementsByTagName('ClaimsExchanges');
        // update / set claimsExchange
        const allClaimsExchangesXML: any = Array.from(orchStepXML.querySelectorAll('ClaimsExchanges>ClaimsExchange'));
        const claimsExchangesJSON = cloneDeep(orchStepJSON['claimsExchanges']);
        allClaimsExchangesXML.forEach((claimsExchangeXML: any) => {
          if (claimsExchangesJSON.length === 0) {
            orchStepXML.removeChild(claimsExchangeXML);
          } else {
            const claimsExchangeJSON = claimsExchangesJSON[0];
            claimsExchangeXML.getAttribute('Id') !== claimsExchangeJSON['id'] &&
              claimsExchangeXML.setAttribute('Id', claimsExchangeJSON['id']);
            claimsExchangeJSON['technicalProfilerefrenceId'] &&
              claimsExchangeXML.getAttribute('TechnicalProfileReferenceId') !==
                claimsExchangeJSON['technicalProfilerefrenceId'] &&
              claimsExchangeXML.setAttribute(
                'TechnicalProfileReferenceId',
                claimsExchangeJSON['technicalProfilerefrenceId'],
              );
            claimsExchangesJSON.splice(0, 1);
          }
        });
        // Add new claims exchanges
        if (claimsExchangesJSON.length > 0) {
          // add claimExchange elements
          claimsExchangesJSON.forEach((exchange: ClaimsExchange) => {
            let claimsExchangeXML = xml.createElementNS(ns, 'ClaimsExchange');
            exchange['id'] && claimsExchangeXML.setAttribute('Id', exchange['id']);
            exchange['technicalProfilerefrenceId'] &&
              claimsExchangeXML.setAttribute('TechnicalProfileReferenceId', exchange['technicalProfilerefrenceId']);
            _claimsExchangesXML[0].appendChild(claimsExchangeXML);
          });
        }
      } else if (
        orchStepXML.querySelectorAll('ClaimsExchanges>ClaimsExchange').length > 0 &&
        orchStepJSON['claimsExchanges'].length == 0
      ) {
        // delete the claimsExchanges if it exists
        orchStepXML.removeChild(orchStepXML.getElementsByTagName('ClaimsExchanges')[0]);
      } else {
        // No claimsExchanges in  XML and no data in JSON
        // console.log('do nothing in claimsExchanges');
      }

      // update/set JourneyList
      if (orchStepJSON['subJourneyToInvoke']) {
        let candidateXML = orchStepXML.querySelectorAll('JourneyList>Candidate');
        // update JourneyList elements
        candidateXML[0].setAttribute('SubJourneyReferenceId', orchStepJSON['subJourneyToInvoke']);
      }
    } else {
      // delete the orchestrationstep - need to test this feature once delete is implemented.
    }
  });
  Object.keys(_jsonSteps).forEach((id: string) => {
    // add this orchestration step
    let orchStepXML = xml.createElementNS(ns, 'OrchestrationStep');
    upsertOrchestrationStep(xml, _jsonSteps[id], orchStepXML);
    let nextTag = xmlJourney.querySelector(
      "OrchestrationSteps>OrchestrationStep[Order='" + (_jsonSteps[id]['order'] + 1) + "']",
    );
    // @ts-ignore
    xmlJourney.querySelector('OrchestrationSteps').insertBefore(orchStepXML, nextTag);
  });
};

const upsertOrchestrationStep = (xml: Document, orchStepJSON: OrchestrationStep, orchStepXML: any) => {
  // set the orchestration step attributes
  orchStepXML.setAttribute('Order', orchStepJSON['order'].toString());
  orchStepJSON.type && orchStepXML.setAttribute('Type', orchStepJSON['type']);
  orchStepJSON.contentDefinitonReferenceId &&
    orchStepXML.setAttribute('ContentDefinitionReferenceId', orchStepJSON['contentDefinitonReferenceId']);
  orchStepJSON.cpimIssuerTechnicalProfileReferenceId &&
    orchStepXML.setAttribute(
      'CpimIssuerTechnicalProfileReferenceId',
      orchStepJSON['cpimIssuerTechnicalProfileReferenceId'],
    );

  // update/set preconditions
  if (orchStepJSON['preconditions'].length > 0) {
    const preconditionsXML = xml.createElementNS(ns, 'Preconditions');
    orchStepJSON['preconditions'].forEach((precondition) => {
      const precXML = xml.createElementNS(ns, 'Precondition');
      upsertPrecondition(xml, precXML, precondition);
      preconditionsXML.appendChild(precXML);
    });
    orchStepXML.appendChild(preconditionsXML);
  }

  // update/set claimProvider Selection
  if (
    orchStepJSON['claimsProviderSelections']['displayOptions'] ||
    orchStepJSON['claimsProviderSelections']['providers'].length > 0
  ) {
    let claimsProviderSelectionsXML = xml.createElementNS(ns, 'ClaimsProviderSelections');
    // update claimsProviderSelections attributes
    orchStepJSON['claimsProviderSelections']['displayOptions'] &&
      claimsProviderSelectionsXML.setAttribute(
        'DisplayOption',
        orchStepJSON['claimsProviderSelections']['displayOptions'],
      );
    // update ClaimsProviderSelections elements
    orchStepJSON['claimsProviderSelections']['providers'].forEach((provider: ProviderSelect) => {
      let claimsProviderSelectionXML = xml.createElementNS(ns, 'ClaimsProviderSelection');
      provider['targetClaimExchangeId'] &&
        claimsProviderSelectionXML.setAttribute('TargetClaimsExchangeId', provider['targetClaimExchangeId']);
      provider['validationClaimsExchange'] &&
        claimsProviderSelectionXML.setAttribute('ValidationClaimsExchangeId', provider['validationClaimsExchange']);
      claimsProviderSelectionsXML.appendChild(claimsProviderSelectionXML);
    });
    orchStepXML.appendChild(claimsProviderSelectionsXML);
  }
  // update/set claimExchanges
  if (orchStepJSON['claimsExchanges'].length > 0) {
    let claimsExchangesXML = xml.createElementNS(ns, 'ClaimsExchanges');
    // update/set claimExchanges elements
    orchStepJSON['claimsExchanges'].forEach((exchange: ClaimsExchange) => {
      let claimsExchangeXML = xml.createElementNS(ns, 'ClaimsExchange');
      exchange['id'] && claimsExchangeXML.setAttribute('Id', exchange['id']);
      exchange['technicalProfilerefrenceId'] &&
        claimsExchangeXML.setAttribute('TechnicalProfileReferenceId', exchange['technicalProfilerefrenceId']);
      claimsExchangesXML.appendChild(claimsExchangeXML);
    });
    orchStepXML.appendChild(claimsExchangesXML);
  }
  // update/set JourneyList
  if (orchStepJSON['subJourneyToInvoke']) {
    let candidateXML = orchStepXML.querySelectorAll('JourneyList>Candidate');
    // update JourneyList elements
    candidateXML[0].setAttribute('SubJourneyReferenceId', orchStepJSON['subJourneyToInvoke']);
  }
};
const addUserJourney = (
  xml: Document,
  journey: UserJourney | SubJourney,
  journeyType: 'UserJourney' | 'SubJourney',
) => {
  const newJourneyXML = xml.getElementsByTagName(journeyType + 's');
  let newXMLJourney: any = xml.createElementNS(ns, journeyType);
  newXMLJourney.setAttribute('Id', journey['id']);
  newJourneyXML[0].appendChild(newXMLJourney);

  // update journey attributes
  if (journeyType === 'UserJourney') {
    // @ts-ignore
    journey.defaultCpimIssuerTechnicalProfileReferenceId &&
      newXMLJourney.setAttribute(
        'DefaultCpimIssuerTechnicalProfileReferenceId',
        // @ts-ignore
        journey.defaultCpimIssuerTechnicalProfileReferenceId,
      );
    // update userJourney element - AuthorizationTechnicalProfile
    // @ts-ignore
    journey.authorizationTP &&
      newXMLJourney[0]
        .querySelector('AuthorizationTechnicalProfile')
        // @ts-ignore
        .setAttribute('ReferenceId', journey.authorizationTP);
  }
  if (journeyType === 'SubJourney') {
    // @ts-ignore
    journey.type && newXMLJourney.setAttribute('Type', journey.type);
  }

  if (journey['orchestrationSteps'].length > 0) {
    let orchStepsXML = xml.createElementNS(ns, 'OrchestrationSteps');
    for (let i = 0; i < journey['orchestrationSteps'].length; i++) {
      const orchStepJSON: OrchestrationStep = journey['orchestrationSteps'][i];
      let orchStepXML = xml.createElementNS(ns, 'OrchestrationStep');
      upsertOrchestrationStep(xml, orchStepJSON, orchStepXML);
      orchStepsXML.appendChild(orchStepXML);
    }
    // add orchSteps to newXMLJourney
    newXMLJourney.appendChild(orchStepsXML);
  } else {
    // delete orchestrationStepsXML - this will not come ideally
  }
};

// XML => The entire XML of a file(rawXMLString).
// journeys => The JSON object of userJourneys[] | subJourneys[]
const createXMLJourneys = (
  xml: Document,
  journeys: UserJourney[] | SubJourney[],
  parser: DOMParser,
  journeyType: 'UserJourney' | 'SubJourney',
  policyName: string,
) => {
  // Create UserJourneys / SubJourneys Element
  if (journeys.length > 0 && xml.getElementsByTagName(journeyType + 's').length === 0) {
    let journeyXML = xml.createElementNS(ns, journeyType + 's');
    const trustFrameworkPolicy: any = xml.getElementsByTagName('TrustFrameworkPolicy')[0];
    journeyType === 'UserJourney'
      ? addXMLElem(trustFrameworkPolicy, journeyXML, ['SubJourneys'])
      : addXMLElem(trustFrameworkPolicy, journeyXML, []);
  }

  const currentXMLUJs = Array.from(xml.querySelectorAll(journeyType + 's>' + journeyType));
  let _list: string[] = [];
  journeys.forEach((journey: UserJourney | SubJourney) => {
    _list.push(journey['id']);
  });
  currentXMLUJs.forEach((ujXML: Element) => {
    let _ujId: string = ujXML.getAttribute('Id') ?? '';
    const _ujIndex: number = journeys.findIndex((journey) => journey['id'] == _ujId);
    if (_ujIndex !== -1) {
      upsertJourney(xml, journeys[_ujIndex], parser, journeyType);
      _list = _list.filter((journey: string) => journey != _ujId);
    } else {
      // If the orignal has journeys (note the 's') element but the latest does not have them, then delete it from the original.
      // (But this case is not possible, so for now throw an error if that happens).
      throw new ErrorReturn(
        `There is no ${journeyType} with Id: ${_ujId} in the latest ${journeyType} List of ${policyName}.`,
      );
    }
  });

  _list.forEach((journeyName: string) => {
    const _index = journeys.findIndex((journey: UserJourney | SubJourney) => journey['id'] === journeyName);
    addUserJourney(xml, journeys[_index], journeyType);
  });
  // END OF ALGORITHM
};
